import { useEffect, useState } from "react";
import { api, currentYM, dispDate, formatRupiah, todayYMD } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { PageLoader } from "@/components/PageLoader";

export default function EmployeeRekap() {
  const { user } = useAuth();
  const [mode, setMode] = useState("month");
  const [ref, setRef] = useState(currentYM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { mode, ref };
    api.get("/rekap/me", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [mode, ref]);

  const onModeChange = (v) => {
    setMode(v);
    if (v === "month") setRef(currentYM());
    else setRef(todayYMD());
  };

  const isHourly = user?.pay_type === "hourly";
  const days = data?.days || [];
  const visibleDays = days.filter((d) => d.in_time || d.on_leave);
  const s = data?.summary || {};

  if (loading && !data) return <PageLoader label="Memuat rekap..." />;

  return (
    <div className="px-4 py-6 max-w-md mx-auto space-y-5">
      <div>
        <p className="overline mb-1">Rekap Presensi</p>
        <h1 className="font-display text-2xl font-bold tracking-tight">Riwayat Anda</h1>
      </div>

      <Tabs value={mode} onValueChange={onModeChange} data-testid="rekap-tabs">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="month" data-testid="tab-month">Bulanan</TabsTrigger>
          <TabsTrigger value="week" data-testid="tab-week">Mingguan</TabsTrigger>
          <TabsTrigger value="day" data-testid="tab-day">Harian</TabsTrigger>
        </TabsList>
      </Tabs>

      <Input
        type={mode === "month" ? "month" : "date"}
        value={ref}
        onChange={(e) => setRef(e.target.value)}
        data-testid="input-rekap-ref"
      />

      <Card className="p-4 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Ringkasan</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {!isHourly && (
            <>
              <Stat label="Total Telat" value={s.total_late_human || "0 Menit"} />
              <Stat label="Potongan" value={formatRupiah(s.total_deduction)} />
              <Stat label="Total Lembur" value={s.total_overtime_human || "0 Menit"} />
              <Stat label="Upah Lembur" value={formatRupiah(s.total_overtime_wage)} />
            </>
          )}
          {isHourly && (
            <>
              <Stat label="Total Jam Kerja" value={s.total_phl_human || "0 Menit"} />
              <Stat label="Upah PHL" value={formatRupiah(s.total_phl_wage)} />
            </>
          )}
          <div className="col-span-2 pt-2 mt-1 border-t border-zinc-200">
            <p className="overline">Netto</p>
            <p className="font-display text-xl font-bold mt-1" data-testid="netto">
              {formatRupiah(s.total_netto)}
            </p>
          </div>
        </div>
      </Card>

      <div className="space-y-2">
        <p className="overline">Detail Hari</p>
        {visibleDays.length === 0 && (
          <p className="text-sm text-zinc-500">Belum ada presensi.</p>
        )}
        {visibleDays.map((d) => (
          <Card key={d.work_date} className="p-3 border border-zinc-200 shadow-none" data-testid={`day-${d.work_date}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-sm">{dispDate(d.work_date)}</p>
                <p className="text-xs text-zinc-500">
                  {d.in_time?.split(" ")[1] || "—"} → {d.out_time?.split(" ")[1] || "—"}
                </p>
              </div>
              <div className="text-right text-xs space-y-0.5">
                {d.on_leave && <p className="text-amber-700 font-semibold uppercase">{d.leave_type}</p>}
                {!isHourly && d.late_min > 0 && (
                  <p className="text-red-600">Telat {d.late_human}</p>
                )}
                {!isHourly && d.overtime_min > 0 && (
                  <p className="text-green-700">Lembur {d.overtime_human}</p>
                )}
                {isHourly && d.phl_minutes > 0 && (
                  <p className="text-zinc-700">{d.overtime_human}</p>
                )}
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-100 text-xs text-zinc-600">
              Netto: <span className="font-semibold text-zinc-900">{formatRupiah(d.netto)}</span>
              {!isHourly && d.multiplier !== 1 && (
                <span className="ml-2 text-purple-700">×{d.multiplier}</span>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs text-zinc-500">{label}</p>
    <p className="font-display font-semibold text-base mt-0.5">{value}</p>
  </div>
);
